import { useState } from 'react'
import { isMobile } from 'react-device-detect'
import { SubmitHandler, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from 'react-query'
import { Link, useNavigate } from 'react-router-dom'
import { useSetRecoilState } from 'recoil'
import styled from 'styled-components'
import { ReactComponent as EtenimLogo } from '../../assets/icons/etenim-logo.svg'
import { ReactComponent as PasswordHidden } from '../../assets/icons/password-hidden.svg'
import { ReactComponent as PasswordShown } from '../../assets/icons/password-shown.svg'
import { Button } from '../../components/Button'
import { PaddedCard } from '../../components/cards/PaddedCard'
import { CheckboxInput } from '../../components/CheckboxInput'
import { CustomText } from '../../components/CustomText'
import { TextInput } from '../../components/TextInput'
import { checkHasPassword, useLogin } from '../../hooks/useLogin'
import { LoginDTO, LoginDtoApplicationEnum } from '../../new-api/api'
import { tokenAtom, userIdAtom } from '../../state/state'
import Colors from '../../utils/Colors'

const backgroundImage = require('../../assets/images/etenim-background.png')

const StyledEtenimLogo = styled(EtenimLogo)`
  position: absolute;
  top: ${isMobile ? 70 : 90}px;
`

const LoginContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  background: url(${backgroundImage}) repeat 0 0;
  background-color: ${Colors.background};
  min-height: 100%;
  background-size: cover;
  background-repeat: no-repeat;
`

const OptionsRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 5px;
`

export const LoginPage = () => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const setToken = useSetRecoilState(tokenAtom)
  const setUserId = useSetRecoilState(userIdAtom)
  const [hasPassword, setHasPassword] = useState<boolean | undefined>(undefined)
  const [hasDifferentPasswordsError, setHasDifferentPasswordsError] = useState(false)
  const [passwordShown, setPasswordShown] = useState(false)
  const {
    register,
    handleSubmit,
    watch,
    setError,
    formState: { errors },
  } = useForm<LoginDTO & { password2?: string }>({
    defaultValues: {
      email: '',
      password: '',
      rememberMe: false,
      application: LoginDtoApplicationEnum.Web,
      device: navigator.userAgent,
    },
    shouldFocusError: true,
  })
  const navigate = useNavigate()
  const { mutateAsync: login, isLoading, error, isError } = useLogin()

  const onSubmit: SubmitHandler<LoginDTO & { password2?: string }> = async (values) => {
    if (hasPassword === false && values.password !== values.password2) {
      setHasDifferentPasswordsError(true)
    } else {
      await login(values, {
        onSuccess: async ({ data }) => {
          if (data.payload?.token) {
            setToken(data?.payload.token)
            setUserId(data.payload.userId)
            navigate('/')
          }
        },
        onSettled: async (data) => {
          await queryClient.resetQueries(['user', data?.data.payload?.userId])
        },
      })
    }
  }

  const identifier = watch('email')

  return (
    <LoginContainer>
      <StyledEtenimLogo />
      <PaddedCard style={{ width: '25%', maxWidth: 450 }}>
        <CustomText style={{ marginBottom: 20 }} fontWeight={400} fontSize={24}>
          {t('customerLogin')}
        </CustomText>
        <TextInput
          name="email"
          label={t('emailOrUsername')}
          placeholder={t('emailOrUsername')}
          options={{
            required: { value: true, message: 'Obavezno' },
          }}
          register={register}
          error={errors?.['email']?.message}
        />
        {hasPassword !== undefined && (
          <TextInput
            name="password"
            placeholder={t('password')}
            label={t('password')}
            type={passwordShown ? 'text' : 'password'}
            options={{
              required: { value: true, message: 'Obavezno' },
              minLength: { value: 8, message: 'Minimalna dužina je 8 znakova' },
            }}
            register={register}
            error={errors?.['password']?.message}
            rightIcon={
              <div onClick={() => setPasswordShown(!passwordShown)}>
                {passwordShown ? (
                  <PasswordShown fill={Colors.primary} stroke={Colors.primary} />
                ) : (
                  <PasswordHidden fill={Colors.primary} stroke={Colors.primary} />
                )}
              </div>
            }
          />
        )}
        {hasPassword === false && (
          <TextInput
            name="password2"
            placeholder={t('confirmPassword')}
            label={t('confirmPassword')}
            type={passwordShown ? 'text' : 'password'}
            options={{
              required: { value: true, message: 'Obavezno' },
              minLength: { value: 8, message: 'Minimalna dužina je 8 znakova' },
            }}
            register={register}
            error={errors?.['password2']?.message}
            rightIcon={
              <div onClick={() => setPasswordShown(!passwordShown)}>
                {passwordShown ? (
                  <PasswordShown fill={Colors.primary} stroke={Colors.primary} />
                ) : (
                  <PasswordHidden fill={Colors.primary} stroke={Colors.primary} />
                )}
              </div>
            }
          />
        )}
        {hasDifferentPasswordsError && (
          <div className={'text-red-400'}>{t('loginErrors.newDoesntMatchConfirmNewPassword')}</div>
        )}
        <OptionsRow>
          <CheckboxInput register={register} name="rememberMe" label="Zapamti me" />
          <Link to="/forgot-password" style={{ textDecoration: 'none', color: Colors.primaryGreen }}>
            <CustomText fontSize={13}>{t('forgottenPassword')}</CustomText>
          </Link>
        </OptionsRow>

        <Button
          text={t('logIn')}
          buttonType="primary"
          onClick={async () => {
            if (hasPassword === undefined && identifier) {
              const resp = await checkHasPassword((identifier || '').trim())
              if (resp?.data !== undefined) {
                setHasPassword(resp.data.payload?.hasPassword)
              }
            } else if (!identifier) {
              setError('email', { message: t('errors.email_or_username_required') })
              return
            } else {
              await handleSubmit(onSubmit)()
            }
          }}
          loading={!!isLoading}
        />
        {isError && (
          <CustomText style={{ color: Colors.lightRed }}>
            {/*@ts-ignore*/}
            {error?.response?.data?.message}
          </CustomText>
        )}
      </PaddedCard>
    </LoginContainer>
  )
}
